/**
 * Content Manager - Logger Utility
 *
 * Sophisticated logging system with configurable log levels,
 * structured logging, and persistent log history.
 */

// =============================================================================
// TYPES
// =============================================================================

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export interface LogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  data?: unknown;
  stack?: string;
}

export interface LoggingConfig {
  enabled: boolean;
  level: LogLevel;
  persistLogs: boolean;
  maxLogEntries: number;
  showTimestamps: boolean;
  categories: {
    api: boolean;
    auth: boolean;
    content: boolean;
    config: boolean;
    realtime: boolean;
  };
}

// =============================================================================
// DEFAULT CONFIG
// =============================================================================

export const DEFAULT_LOGGING_CONFIG: LoggingConfig = {
  enabled: false,
  level: 'INFO',
  persistLogs: true,
  maxLogEntries: 500,
  showTimestamps: true,
  categories: {
    api: true,
    auth: true,
    content: true,
    config: true,
    realtime: true,
  },
};

// =============================================================================
// LOG LEVEL PRIORITY
// =============================================================================

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// =============================================================================
// LOG STYLING
// =============================================================================

const LOG_STYLES: Record<LogLevel, { color: string; background: string; icon: string }> = {
  DEBUG: { color: '#6b7280', background: '#f3f4f6', icon: '🔍' },
  INFO: { color: '#3b82f6', background: '#eff6ff', icon: 'ℹ️' },
  WARN: { color: '#f59e0b', background: '#fffbeb', icon: '⚠️' },
  ERROR: { color: '#ef4444', background: '#fef2f2', icon: '❌' },
};

// =============================================================================
// LOGGER CLASS
// =============================================================================

type LogCategory = keyof LoggingConfig['categories'];

class Logger {
  private config: LoggingConfig = DEFAULT_LOGGING_CONFIG;
  private logs: LogEntry[] = [];
  private listeners: Set<(logs: LogEntry[]) => void> = new Set();
  private readonly STORAGE_KEY = 'content-manager-logs';

  constructor() {
    this.loadLogs();
  }

  // ===========================================================================
  // CONFIGURATION
  // ===========================================================================

  /**
   * Update logger configuration (called by configStore when settings change)
   */
  setConfig(config: Partial<LoggingConfig>): void {
    this.config = { ...this.config, ...config };

    if (config.categories) {
      this.config.categories = { ...this.config.categories, ...config.categories };
    }

    // If logging just got enabled, log it
    if (config.enabled === true && this.config.enabled) {
      this.info('config', 'Logging enabled', { level: this.config.level });
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): LoggingConfig {
    return { ...this.config };
  }

  /**
   * Check if a log level should be shown
   */
  private shouldLog(level: LogLevel, category?: LogCategory): boolean {
    if (!this.config.enabled) return false;

    // Check category filter
    if (category && !this.config.categories[category]) return false;

    // Check log level
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.config.level];
  }

  // ===========================================================================
  // LOGGING METHODS
  // ===========================================================================

  /**
   * Log a debug message
   */
  debug(category: LogCategory, message: string, data?: unknown): void {
    this.log('DEBUG', category, message, data);
  }

  /**
   * Log an info message
   */
  info(category: LogCategory, message: string, data?: unknown): void {
    this.log('INFO', category, message, data);
  }

  /**
   * Log a warning message
   */
  warn(category: LogCategory, message: string, data?: unknown): void {
    this.log('WARN', category, message, data);
  }

  /**
   * Log an error message
   */
  error(category: LogCategory, message: string, error?: unknown): void {
    const errorData = error instanceof Error
      ? { message: error.message, stack: error.stack }
      : error;

    this.log('ERROR', category, message, errorData, error instanceof Error ? error.stack : undefined);
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, category: LogCategory, message: string, data?: unknown, stack?: string): void {
    if (!this.shouldLog(level, category)) return;

    const entry: LogEntry = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      stack,
    };

    // Add to log history
    this.logs.push(entry);

    // Trim if exceeds max
    if (this.logs.length > this.config.maxLogEntries) {
      this.logs = this.logs.slice(-this.config.maxLogEntries);
    }

    // Persist if enabled
    if (this.config.persistLogs) {
      this.saveLogs();
    }

    // Notify listeners
    this.notifyListeners();

    // Output to console
    this.outputToConsole(entry);
  }

  /**
   * Output log entry to browser console
   */
  private outputToConsole(entry: LogEntry): void {
    const style = LOG_STYLES[entry.level];
    const timestamp = this.config.showTimestamps
      ? `[${new Date(entry.timestamp).toLocaleTimeString()}] `
      : '';

    const prefix = `${style.icon} ${timestamp}[${entry.category.toUpperCase()}]`;
    const consoleMethod = entry.level === 'ERROR' ? 'error'
      : entry.level === 'WARN' ? 'warn'
      : entry.level === 'DEBUG' ? 'debug'
      : 'log';

    const styles = `color: ${style.color}; font-weight: bold;`;

    if (entry.data) {
      console[consoleMethod](`%c${prefix} ${entry.message}`, styles, entry.data);
    } else {
      console[consoleMethod](`%c${prefix} ${entry.message}`, styles);
    }

    if (entry.stack) {
      console[consoleMethod]('%cStack trace:', 'color: #6b7280', entry.stack);
    }
  }

  // ===========================================================================
  // LOG HISTORY
  // ===========================================================================

  /**
   * Get all log entries
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Get logs filtered by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter((log) => log.level === level);
  }

  /**
   * Get logs filtered by category
   */
  getLogsByCategory(category: LogCategory): LogEntry[] {
    return this.logs.filter((log) => log.category === category);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
    this.saveLogs();
    this.notifyListeners();

    if (this.config.enabled) {
      this.info('config', 'Logs cleared');
    }
  }

  /**
   * Export logs as JSON
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Download logs as a file
   */
  downloadLogs(): void {
    const data = this.exportLogs();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `content-manager-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ===========================================================================
  // PERSISTENCE
  // ===========================================================================

  /**
   * Save logs to localStorage
   */
  private saveLogs(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.logs));
    } catch {
      // Storage full or unavailable - silently ignore
    }
  }

  /**
   * Load logs from localStorage
   */
  private loadLogs(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch {
      // Invalid data - start fresh
      this.logs = [];
    }
  }

  // ===========================================================================
  // SUBSCRIPTIONS
  // ===========================================================================

  /**
   * Subscribe to log updates
   */
  subscribe(callback: (logs: LogEntry[]) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of log changes
   */
  private notifyListeners(): void {
    const logs = this.getLogs();
    this.listeners.forEach((callback) => callback(logs));
  }

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  /**
   * Generate a unique ID for log entries
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get log statistics
   */
  getStats(): { total: number; byLevel: Record<LogLevel, number>; byCategory: Record<string, number> } {
    const byLevel: Record<LogLevel, number> = { DEBUG: 0, INFO: 0, WARN: 0, ERROR: 0 };
    const byCategory: Record<string, number> = {};

    this.logs.forEach((log) => {
      byLevel[log.level]++;
      byCategory[log.category] = (byCategory[log.category] || 0) + 1;
    });

    return {
      total: this.logs.length,
      byLevel,
      byCategory,
    };
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

export const logger = new Logger();

// =============================================================================
// CONVENIENCE EXPORTS
// =============================================================================

export default logger;
